import React from 'react';
import { Box, Container, Typography, Link, Divider, useTheme, Stack, IconButton, Grid } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import { useTenant } from '../contexts/TenantContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSubmissionWindow } from '../hooks/useSubmissionWindow';
import { Translations } from '../i18n';

// Define wrapper components to fix TypeScript issues with Grid
const GridContainer = (props: any) => <Grid container {...props} />;
const GridItem = (props: any) => <Grid item {...props} />;

// Tenant-specific footer configurations
interface FooterConfig {
  title: string;
  description: string;
  address: string[];
  projectEmail: string;
  supportEmail: string;
  phone?: string; // Optional phone number
  organization: string;
}

const getFooterConfig = (tenantId: string, t: Translations): FooterConfig => {
  const configs: Record<string, FooterConfig> = {
    severance: {
      title: "Severance Town Center",
      description: "A community-driven revitalization project for the Severance Town Center, focusing on creating a vibrant mixed-use district that serves the community's needs and aspirations.",
      address: ["1046 New York Ave", "Huntington Station, NY 11746"],
      projectEmail: "rporter@mpactcollective.com",
      supportEmail: "josh@endstation.ai",
      phone: "(631) 944-3101",
      organization: "MPact Collective"
    },
    evanston: {
      title: "Evanston PAW",
      description: t.footer.evanstonDescription,
      address: [
        "Evanston PAW",
        "c/o Economic Development, 3rd Floor",
        "Lorraine H. Morton City Hall",
        "909 Davis Street",
        "Evanston, IL 60201"
      ],
      projectEmail: "evanston@pa2w.org",
      supportEmail: "josh@endstation.ai",
      organization: "MPact Collective & Propvizer"
    }
  };
  
  return configs[tenantId] || configs.severance;
};

const Footer: React.FC = () => {
  const theme = useTheme();
  const { tenantConfig } = useTenant();
  const { t } = useLanguage();
  const { isClosed: submissionsClosed } = useSubmissionWindow();
  const currentYear = new Date().getFullYear();

  // Get tenant-specific footer content
  const footerConfig = getFooterConfig(tenantConfig?.tenant_id || 'severance', t);
  
  return (
    <Box
      component="footer"
      sx={{
        py: 6,
        mt: 8,
        backgroundColor: 'primary.dark',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '4px',
          background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: 'radial-gradient(circle at 20% 150%, rgba(40, 70, 100, 0.4) 0%, transparent 60%)',
          zIndex: 0,
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <GridContainer spacing={4} justifyContent="space-between">
          <GridItem xs={12} md={4}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                mb: 3,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 60,
                  height: 3,
                  background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                  borderRadius: '2px',
                }
              }}
            >
              {footerConfig.title}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 2, 
                color: 'rgba(255,255,255,0.8)',
                maxWidth: 300,
                lineHeight: 1.8
              }}
            >
              {footerConfig.description}
            </Typography>
          </GridItem>
          
          <GridItem xs={12} md={4}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                mb: 3,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 60,
                  height: 3,
                  background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                  borderRadius: '2px',
                }
              }}
            >
              {t.footer.contact}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', mb: 2.5, alignItems: 'flex-start' }}>
                <LocationOnIcon sx={{ color: theme.palette.secondary.main, mr: 2, mt: 0.5 }} />
                <Box>
                  {footerConfig.address.map((line, index) => (
                    <Typography 
                      key={index}
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255,255,255,0.8)',
                        lineHeight: 1.4,
                        mb: index < footerConfig.address.length - 1 ? 0.5 : 0
                      }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', mb: 2.5, alignItems: 'flex-start' }}>
                <EmailIcon sx={{ color: theme.palette.secondary.main, mr: 2, mt: 0.5 }} />
                <Link 
                  href={`mailto:${footerConfig.projectEmail}`} 
                  sx={{ 
                    color: 'rgba(255,255,255,0.8)',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    '&:hover': { 
                      color: theme.palette.secondary.light,
                      textDecoration: 'underline' 
                    }
                  }}
                >
                  {footerConfig.projectEmail}
                </Link>
              </Box>
              <Box sx={{ display: 'flex', mb: 2.5, alignItems: 'flex-start' }}>
                <EmailIcon sx={{ color: theme.palette.secondary.main, mr: 2, mt: 0.5 }} />
                <Link 
                  href={`mailto:${footerConfig.supportEmail}`} 
                  sx={{ 
                    color: 'rgba(255,255,255,0.8)',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    '&:hover': { 
                      color: theme.palette.secondary.light,
                      textDecoration: 'underline' 
                    }
                  }}
                >
                  {t.footer.needHelp} {footerConfig.supportEmail}
                </Link>
              </Box>
              {footerConfig.phone && (
                <Box sx={{ display: 'flex', mb: 2.5, alignItems: 'flex-start' }}>
                  <PhoneIcon sx={{ color: theme.palette.secondary.main, mr: 2, mt: 0.5 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {footerConfig.phone}
                  </Typography>
                </Box>
              )}
            </Box>
          </GridItem>
          
          <GridItem xs={12} md={4}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                mb: 3,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 60,
                  height: 3,
                  background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                  borderRadius: '2px',
                }
              }}
            >
              {t.footer.quickLinks}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {[
                { name: t.header.about, path: '/about' },
                { name: t.header.ideas, path: '/home' },
                { name: t.header.submitIdea, path: '/submit' },
                { name: t.footer.privacyPolicy, path: '/privacy' }
              ]
                .filter((link) => !(submissionsClosed && link.path === '/submit'))
                .map((link) => (
                <Link 
                  key={link.name}
                  component={RouterLink} 
                  to={link.path} 
                  sx={{ 
                    color: 'rgba(255,255,255,0.8)', 
                    mb: 2,
                    textDecoration: 'none',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      color: theme.palette.secondary.light,
                      transform: 'translateX(6px)'
                    },
                  }}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      width: 6, 
                      height: 6, 
                      borderRadius: '50%', 
                      backgroundColor: theme.palette.secondary.main,
                      display: 'inline-block',
                      mr: 1.5
                    }}
                  />
                  {link.name}
                </Link>
              ))}
            </Box>
          </GridItem>
        </GridContainer>
        
        <Divider sx={{ mt: 5, mb: 4, borderColor: 'rgba(255,255,255,0.15)' }} />
        
        <Box 
          sx={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 2 },
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            © {currentYear} {footerConfig.organization}. {t.footer.allRightsReserved}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', display: { xs: 'none', sm: 'block' } }}>
            •
          </Typography>
          <Link
            component={RouterLink}
            to="/privacy"
            sx={{
              color: 'rgba(255,255,255,0.6)',
              textDecoration: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                color: theme.palette.secondary.light,
                textDecoration: 'underline'
              }
            }}
          >
            {t.footer.privacyPolicy}
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 